import { useMemo, useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { toggleDsPick, setDsViewPreference } from 'redux/mode/actions';
import useScrollMemory from 'hooks/useScrollMemory';
import Icon, { IconType } from 'components/Icon';
import ViewToggle from 'components/Widget/ViewToggle';
import { DesignSystemToken, TokenCategory } from 'designSystem/types';
import { getColorString } from 'designSystem/content/util';
import { groupColorsForGrid, ColorFamily } from '../ColorsView/util';
import TokenGrid from '../../tokens/TokenGrid';
import TokenTable from '../../tokens/TokenTable';
import SpacingScale from '../../tokens/SpacingScale';
import ElevationScale from '../../tokens/ElevationScale';
import BorderRadiusScale from '../../tokens/BorderRadiusScale';
import OpacityScale from '../../tokens/OpacityScale';
import TypographySection from '../../tokens/TypographySection';
import FilterPills from '../../FilterPills';
import * as Base from '../../styles';
import * as ColorsS from '../ColorsView/styles';
import * as S from './styles';
import { useTheme } from 'styled-components';

// Ordered category definitions for the scrolling page
const CATEGORY_ORDER: { key: TokenCategory; label: string }[] = [
	{ key: 'color', label: 'Colors' },
	{ key: 'typography', label: 'Typography' },
	{ key: 'spacing', label: 'Spacing' },
	{ key: 'elevation', label: 'Elevation' },
	{ key: 'borderRadius', label: 'Border Radius' },
	{ key: 'opacity', label: 'Opacity' },
	{ key: 'motion', label: 'Motion' },
	{ key: 'other', label: 'Other' },
];

const AllTokensView = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const { designSystemContent, selectedCategories, activeTab, viewMode, colorsGridVisible } =
		useSelector(
			(r: RootReducer) => ({
				designSystemContent: r.mode.designSystemContent,
				selectedCategories: r.mode.designSystemFilters.theme.selectedCategories,
				activeTab: selectActiveDsTab(r.mode),
				viewMode: r.mode.dsViewPreferences.tokensViewMode ?? 'sections',
				colorsGridVisible: r.mode.dsViewPreferences.colorsGridVisible ?? true,
			}),
			shallowEqual
		);

	const scrollMemoryRef = useScrollMemory(activeTab?.id ?? null);
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
	const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

	// Combine scrollMemory callback ref with our local ref
	const setScrollRef = useCallback(
		(el: HTMLDivElement | null) => {
			scrollContainerRef.current = el;
			scrollMemoryRef(el);
		},
		[scrollMemoryRef]
	);

	//----------------------------------------------------------------------
	// UTILITY
	//----------------------------------------------------------------------

	// Get token counts and filter pills data
	const availableCategories = useMemo(() => {
		if (!designSystemContent?.theme) return [];
		return CATEGORY_ORDER.map(({ key, label }) => ({
			value: key,
			label,
			count: designSystemContent.theme!.byCategory[key]?.length || 0,
		}));
	}, [designSystemContent?.theme]);

	// Determine which categories to show, always in CATEGORY_ORDER
	const visibleCategories = useMemo(() => {
		if (selectedCategories.length === 0) return CATEGORY_ORDER.map((c) => c.key);
		const selected = new Set(selectedCategories);
		return CATEGORY_ORDER.map((c) => c.key).filter((k) => selected.has(k));
	}, [selectedCategories]);

	// Get tokens grouped by category, only base (non-mode-override) tokens
	const tokensByCategory = useMemo(() => {
		if (!designSystemContent?.theme) return {} as Record<TokenCategory, DesignSystemToken[]>;
		const result: Record<string, DesignSystemToken[]> = {};
		for (const { key } of CATEGORY_ORDER) {
			const tokens = designSystemContent.theme.byCategory[key] || [];
			result[key] = tokens.filter((t) => !t.mode);
		}
		return result as Record<TokenCategory, DesignSystemToken[]>;
	}, [designSystemContent?.theme]);

	// Color grid data
	const colorGridData = useMemo(() => {
		const colorTokens = tokensByCategory.color || [];
		return groupColorsForGrid(colorTokens);
	}, [tokensByCategory.color]);

	// All visible tokens flattened (for list mode)
	const allVisibleTokens = useMemo(() => {
		const tokens: DesignSystemToken[] = [];
		for (const cat of visibleCategories) {
			tokens.push(...(tokensByCategory[cat] || []));
		}
		return tokens;
	}, [visibleCategories, tokensByCategory]);

	//----------------------------------------------------------------------
	// SCROLL-SPY
	//----------------------------------------------------------------------

	const setSectionRef = useCallback((key: string, el: HTMLElement | null) => {
		if (el) {
			sectionRefs.current.set(key, el);
		} else {
			sectionRefs.current.delete(key);
		}
	}, []);

	useEffect(() => {
		if (viewMode !== 'sections') return;

		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return;

		const observer = new IntersectionObserver(
			(entries) => {
				let topEntry: IntersectionObserverEntry | null = null;
				for (const entry of entries) {
					if (entry.isIntersecting) {
						if (!topEntry || entry.boundingClientRect.top < topEntry.boundingClientRect.top) {
							topEntry = entry;
						}
					}
				}
				if (topEntry?.target) {
					setActiveSectionId(topEntry.target.getAttribute('data-category'));
				}
			},
			{
				root: scrollContainer,
				rootMargin: '-10% 0px -60% 0px',
				threshold: 0,
			}
		);

		sectionRefs.current.forEach((el, key) => {
			if (el && document.contains(el)) {
				observer.observe(el);
			} else {
				sectionRefs.current.delete(key);
			}
		});

		return () => observer.disconnect();
	}, [viewMode, visibleCategories]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const selectedPicks = useSelector((r: RootReducer) => r.mode.selectedDsPicks);

	const isTokenSelected = (token: DesignSystemToken) =>
		selectedPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	const onSwatchClick = (token: DesignSystemToken) => {
		dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }));
	};

	const onToggleColorsGrid = () => {
		dispatch(setDsViewPreference({ colorsGridVisible: !colorsGridVisible }));
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const renderViewToggle = () => (
		<ViewToggle
			options={[
				{ value: 'sections', icon: IconType.GRID, title: 'Sections view' },
				{ value: 'list', icon: IconType.STACK, title: 'List view' },
			]}
			value={viewMode}
			onChange={(v) => dispatch(setDsViewPreference({ tokensViewMode: v as 'sections' | 'list' }))}
		/>
	);

	const renderColorRampGrid = (families: ColorFamily[], allShades: string[]) => {
		if (families.length === 0) return null;
		return (
			<ColorsS.GridWrapper>
				<ColorsS.ColorGrid
					style={{
						gridTemplateColumns: `auto repeat(${allShades.length}, minmax(48px, 80px))`,
					}}
				>
					<div />
					{allShades.map((shade) => (
						<ColorsS.HeaderCell key={shade}>{shade}</ColorsS.HeaderCell>
					))}

					{families.map((family) => {
						const shadeMap = new Map(family.shades.map((s) => [s.shade, s.token]));
						return (
							<Fragment key={family.family}>
								<ColorsS.FamilyLabel>{family.family}</ColorsS.FamilyLabel>
								{allShades.map((shade) => {
									const token = shadeMap.get(shade);
									if (!token) {
										return <ColorsS.EmptyCell key={`${family.family}-${shade}`} />;
									}
									const color = getColorString(token);
									const selected = isTokenSelected(token);
									return (
										<ColorsS.SwatchCell
											key={token.id}
											type="button"
											$color={color}
											$selected={selected}
											aria-pressed={selected}
											onClick={() => onSwatchClick(token)}
										>
											<ColorsS.SwatchTooltip>
												{token.cssVariable}
												<br />
												{color}
											</ColorsS.SwatchTooltip>
										</ColorsS.SwatchCell>
									);
								})}
							</Fragment>
						);
					})}
				</ColorsS.ColorGrid>
			</ColorsS.GridWrapper>
		);
	};

	const renderUngroupedTiles = (tokens: DesignSystemToken[]) => {
		if (tokens.length === 0) return null;
		return (
			<ColorsS.UngroupedSection>
				{colorsGridVisible && colorGridData.families.length > 0 && (
					<ColorsS.UngroupedHeader>
						<ColorsS.UngroupedTitle>Other Colors</ColorsS.UngroupedTitle>
					</ColorsS.UngroupedHeader>
				)}
				<ColorsS.UngroupedGrid>
					{tokens.map((token) => {
						const color = getColorString(token);
						const selected = isTokenSelected(token);
						return (
							<ColorsS.UngroupedSwatch
								key={token.id}
								type="button"
								$selected={selected}
								aria-pressed={selected}
								onClick={() => onSwatchClick(token)}
							>
								<ColorsS.UngroupedSwatchTooltip>{color}</ColorsS.UngroupedSwatchTooltip>
								<ColorsS.UngroupedSwatchColor $color={color} />
								<ColorsS.UngroupedSwatchLabel>
									<ColorsS.UngroupedSwatchName>{token.name}</ColorsS.UngroupedSwatchName>
									<ColorsS.UngroupedSwatchVariable>
										{token.cssVariable}
									</ColorsS.UngroupedSwatchVariable>
								</ColorsS.UngroupedSwatchLabel>
							</ColorsS.UngroupedSwatch>
						);
					})}
				</ColorsS.UngroupedGrid>
			</ColorsS.UngroupedSection>
		);
	};

	const renderColorSection = () => {
		const colorTokens = tokensByCategory.color || [];
		if (colorTokens.length === 0) return <p>No color tokens found.</p>;

		const tileTokens = colorsGridVisible ? colorGridData.ungrouped : colorTokens;

		return (
			<ColorsS.Base>
				{colorsGridVisible && renderColorRampGrid(colorGridData.families, colorGridData.allShades)}
				{renderUngroupedTiles(tileTokens)}
			</ColorsS.Base>
		);
	};

	const renderCategorySection = (categoryKey: TokenCategory) => {
		const tokens = tokensByCategory[categoryKey] || [];
		if (tokens.length === 0) return null;

		const label = CATEGORY_ORDER.find((c) => c.key === categoryKey)?.label ?? categoryKey;

		return (
			<S.CategorySection
				key={categoryKey}
				ref={(el) => setSectionRef(categoryKey, el)}
				data-category={categoryKey}
			>
				<S.CategoryHeader>
					<S.CategoryTitle>{label}</S.CategoryTitle>
					{categoryKey === 'color' && colorGridData.families.length > 0 && (
						<S.TogglePill type="button" $active={colorsGridVisible} onClick={onToggleColorsGrid}>
							<Icon
								type={colorsGridVisible ? IconType.CHECKBOX_ON : IconType.CHECKBOX_OFF}
								width={16}
								height={16}
								color={colorsGridVisible ? theme.gray[1000] : undefined}
							/>
							Color Grid
						</S.TogglePill>
					)}
				</S.CategoryHeader>
				{categoryKey === 'color' ? (
					renderColorSection()
				) : categoryKey === 'typography' ? (
					<TypographySection tokens={tokens} />
				) : categoryKey === 'spacing' ? (
					<SpacingScale tokens={tokens} />
				) : categoryKey === 'elevation' ? (
					<ElevationScale tokens={tokens} />
				) : categoryKey === 'borderRadius' ? (
					<BorderRadiusScale tokens={tokens} />
				) : categoryKey === 'opacity' ? (
					<OpacityScale tokens={tokens} />
				) : (
					<TokenGrid tokens={tokens} />
				)}
			</S.CategorySection>
		);
	};

	return (
		<S.ScrollContainer ref={setScrollRef}>
			<S.HeaderArea>
				<Base.SectionHeader>
					<Base.SectionTitle>Theme</Base.SectionTitle>
					{allVisibleTokens.length > 0 && renderViewToggle()}
				</Base.SectionHeader>
			</S.HeaderArea>
			<FilterPills
				section="theme"
				categories={availableCategories}
				sticky
				activeSectionId={activeSectionId}
			/>
			<Base.SectionContent>
				{allVisibleTokens.length === 0 ? (
					<p>
						No theme tokens found
						{selectedCategories.length > 0 && ` matching selected categories`}.
					</p>
				) : viewMode === 'list' ? (
					visibleCategories.map((cat) => {
						const tokens = tokensByCategory[cat] || [];
						if (tokens.length === 0) return null;
						const label = CATEGORY_ORDER.find((c) => c.key === cat)?.label ?? cat;
						return (
							<S.ListSection key={cat}>
								<S.ListSectionHeader>{label}</S.ListSectionHeader>
								<TokenTable tokens={tokens} />
							</S.ListSection>
						);
					})
				) : (
					visibleCategories.map((cat) => renderCategorySection(cat))
				)}
			</Base.SectionContent>
		</S.ScrollContainer>
	);
};

export default AllTokensView;
